import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, RefreshCw, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import IgManageShell from "@/components/IgManageShell";

export default function InstagramTeamAccess() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState({ templates: true, uploads: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows(await api.igTeamAccessList()); } catch { toast.error("Could not load team access."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!email.trim()) { toast.error("Enter the teammate's email."); return; }
    if (!perms.templates && !perms.uploads) { toast.error("Grant at least one permission."); return; }
    setSaving(true);
    try {
      await api.igTeamAccessAdd({ email: email.trim(), templates: perms.templates, uploads: perms.uploads });
      toast.success("Access granted");
      setEmail(""); setPerms({ templates: true, uploads: true }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not grant access."); }
    setSaving(false);
  };

  const revoke = async (r) => {
    if (!window.confirm(`Revoke Instagram access for ${r.email}?`)) return;
    try { await api.igTeamAccessRemove(r.id); toast.success("Access revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  return (
    <IgManageShell need="admin" title="Instagram Team Access">
      <div data-testid="ig-team-access-page">
        <p className="mb-4 max-w-2xl text-sm text-[#A1A1AA]">Grant a registered teammate the <span className="font-bold text-white">Instagram Content Manager</span> role. They'll only be able to open the Template Manager and/or User Upload Review pages — never Billing, Users, Payments or other admin tools. The teammate must have signed in at least once.</p>

        <div className="mb-6 rounded-xl border border-[#E1306C]/30 bg-white/[0.02] p-4" data-testid="ig-add-team-form">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-white"><UserPlus size={16} className="text-[#E1306C]" /> Add Team Member</div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <label className="mb-1 block text-xs font-semibold text-[#A1A1AA]">Teammate email</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="teammate@example.com" data-testid="ig-team-email"
                className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-[#E1306C]/60" />
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-white" data-testid="ig-perm-templates">
              <input type="checkbox" checked={perms.templates} onChange={(e) => setPerms((p) => ({ ...p, templates: e.target.checked }))} /> Manage Templates
            </label>
            <label className="flex items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold text-white" data-testid="ig-perm-uploads">
              <input type="checkbox" checked={perms.uploads} onChange={(e) => setPerms((p) => ({ ...p, uploads: e.target.checked }))} /> Review User Uploads
            </label>
            <button onClick={add} disabled={saving} data-testid="ig-team-save" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Grant Access
            </button>
          </div>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-bold text-white">Team members</div>
          <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#E1306C]" /></div> : (
          <div className="space-y-2" data-testid="ig-team-list">
            {rows.length === 0 && <div className="rounded-lg border border-white/10 bg-white/[0.02] py-8 text-center text-sm text-[#666]">No team members yet.</div>}
            {rows.map((r) => (
              <div key={r.id} data-testid={`ig-team-row-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-bold text-white">{r.email}</div>
                  {r.name && <div className="text-[11px] text-[#666]">{r.name}</div>}
                </div>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.permissions?.templates ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/5 text-[#666]"}`}>{r.permissions?.templates ? <Check size={10} /> : <X size={10} />} Templates</span>
                <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${r.permissions?.uploads ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/5 text-[#666]"}`}>{r.permissions?.uploads ? <Check size={10} /> : <X size={10} />} Uploads</span>
                <button onClick={() => revoke(r)} data-testid={`ig-team-revoke-${r.id}`} className="rounded-lg border border-white/10 p-1.5 text-red-300 hover:border-red-400/50"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </IgManageShell>
  );
}
